import React from "react";
import PropTypes from "prop-types";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
  Badge,
  Card,
  CardBody,
  Row,
  Col,
  Progress
} from "reactstrap";
import { format, parseISO } from 'date-fns';
import { ar } from 'date-fns/locale';
import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import { FaMapMarkerAlt, FaPhone, FaUser, FaInfoCircle, FaHome, FaClock } from "react-icons/fa";
import appwritPointes from "helpers/appwrite/appwritePointes";

const libraries = ["places"];

const LoadingContainer = () => (
  <div className="text-center py-4">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
    <p className="mt-2">جاري تحميل الخريطة...</p>
  </div>
);

const AddressMap = ({ latitude, longitude }) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: appwritPointes.googleMapId,
    libraries,
  });

  const mapContainerStyle = {
    height: "200px",
    width: "100%",
    borderRadius: "8px"
  };

  const center = {
    lat: parseFloat(latitude) || 24.7136,
    lng: parseFloat(longitude) || 46.6753
  };

  if (loadError) return <div className="text-danger">خطأ في تحميل الخريطة</div>;
  if (!isLoaded) return <LoadingContainer />;

  return (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      zoom={15}
      center={center}
      options={{
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
      }}
    >
      <Marker position={center} />
    </GoogleMap>
  );
};

const OrdersModal = ({ isOpen, toggle, orderData }) => {
  if (!orderData) return null;

  const user = orderData.app_user_id || {};
  const provider = orderData.service_providers_id || {};
  const service = orderData.serviceProviderServices || {};
  const address = orderData.user_address_id || {}; // Will be null if not available
  
  const formatFirebaseDate = (timestamp) => {
    if (!timestamp?.seconds) return 'غير محدد';
    const date = new Date(timestamp.seconds * 1000);
    return format(date, 'PPPPp', { locale: ar });
  };

  const formatAddress = () => {
    if (!address) return 'لم يتم تحديد عنوان';
    return (
      <>
        {address.notes && <div className="mb-1"><FaInfoCircle className="me-2" /> {address.notes}</div>}
        {address.isDefault && <Badge color="info" className="mb-2">العنوان الافتراضي</Badge>}
        <div className="text-muted small">
          <div>الإحداثيات: {address.latitude?.toFixed(6)}, {address.longitude?.toFixed(6)}</div>
          <div>آخر تحديث: {formatFirebaseDate(address.timestamp)}</div>
        </div>
      </>
    );
  };


 // Define order status steps with their corresponding statuses
 const statusSteps = [
  { status: 'pending', label: 'قيد الانتظار', color: 'warning' },
  { status: 'approved', label: 'تم القبول', color: 'info' },
  { status: 'progress', label: 'قيد التنفيذ', color: 'primary' },
  { status: 'completed', label: 'مكتمل', color: 'success' },
  { status: 'rejected', label: 'ملغى', color: 'danger' },


];

// Find the current status index
const currentStatusIndex = statusSteps.findIndex(step => 
  step.status === orderData.booking_status
);

// Calculate progress percentage (for smooth animation)
const progressPercentage = ((currentStatusIndex + 1) / statusSteps.length) * 100;

// Status progress bar component
const StatusProgressBar = () => (
  <div className="status-progress mb-4">
    <Progress
      animated
      striped
      value={progressPercentage}
      color={statusSteps[currentStatusIndex]?.color || 'primary'}
      className="mb-3"
    />
    <div className="d-flex justify-content-between">
      {statusSteps.map((step, index) => (
        <div 
          key={step.status}
          className={`text-center ${index <= currentStatusIndex ? 'text-' + step.color : 'text-muted'}`}
          style={{ flex: 1 }}
        >
          <div className={`mb-1 ${index <= currentStatusIndex ? 'fw-bold' : ''}`}>
            {step.label}
          </div>
          <div className={`status-indicator ${index <= currentStatusIndex ? 'active' : ''}`}>
            {index <= currentStatusIndex ? (
              <i className={`bx bx-check-circle text-${step.color}`} />
            ) : (
              <i className="bx bx-circle" />
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
);




  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle} className="border-bottom">
        <h5 className="mb-0">تفاصيل الطلب #{orderData.id}</h5>
      </ModalHeader>
      <ModalBody>
         {/* Add the status progress bar at the top */}
         <StatusProgressBar />

        {/* Order Status Section */}
          <Row className="mb-4">
            <Col md={6}>
              <Card className="border-0 shadow-sm mb-3">
                <CardBody>
            <h6 className="d-flex align-items-center mb-3">
              <FaClock className="me-2 text-primary" />
              حالة الطلب
            </h6>
            <div className="d-flex justify-content-between">
              <div>
                <p className="mb-1"><strong>الحالة:</strong></p>
                <p className="mb-1"><strong>تاريخ التنفيذ:</strong></p>
                <p className="mb-0"><strong>عدد المستفيدين:</strong></p>
              </div>
              <div className="text-end">
                <Badge color={getStatusColor(orderData.booking_status)} className="mb-1">
                  {translateStatus(orderData.booking_status)}
                </Badge>
                <p className="mb-1 text-primary">
                  {orderData.booking_execute_date 
              ? format(parseISO(orderData.booking_execute_date), 'PPPPp', { locale: ar }) 
              : 'غير محدد'}
                </p>
                <p className="mb-0">{orderData.The_number_of_beneficiaries || 'غير محدد'}</p>
              </div>
            </div>
                </CardBody>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="border-0 shadow-sm">
                <CardBody>
            <h6 className="d-flex align-items-center mb-3">
              <FaClock className="me-2 text-primary" />
              معلومات الدفع
            </h6>
            <div className="d-flex justify-content-between">
              <div>
                <p className="mb-1"><strong>طريقة الدفع:</strong></p>
                <p className="mb-0"><strong>حالة الدفع:</strong></p>
              </div>
              <div className="text-end">
                <p className="mb-1">{orderData.paymentMethod || 'غير محدد'}</p>
                <Badge color={getPaymentStatusColor(orderData.paymentStatus)}>
                  {translatePaymentStatus(orderData.paymentStatus)}
                </Badge>
              </div>
            </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Service Information */}
        <Card className="border-0 shadow-sm mb-4">
          <CardBody>
            <h6 className="d-flex align-items-center mb-3">
              <FaHome className="me-2 text-primary" />
              معلومات الخدمة
            </h6>
            <Row>
              <Col md={6}>
              <p><strong>الخدمة:</strong> {orderData?.serviceProviderServices?.name || 'غير محدد'}</p>
              <p><strong>القسم:</strong> {orderData.category_type || 'غير محدد'}</p>
              </Col>
              <Col md={6}>
                <p><strong>التعليقات:</strong></p>
                <div className="bg-light p-2 rounded">
                  {orderData.comment || 'لا يوجد تعليقات'}
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Pricing Section */}
        <Card className="border-0 shadow-sm mb-4">
          <CardBody>
            <h6 className="mb-3">تفاصيل السعر</h6>
            <Table borderless className="mb-0">
              <tbody>
                <tr>
                  <td><strong>الإجمالي يشمل ضريبة القيمة المضافة</strong></td>
                  <td className="text-end">{orderData.total_after_discount || 0} ريال</td>
                </tr>
                <tr>
                  <td><strong> نسبة الضريبة المضافة</strong></td>
                  <td className="text-end">15%</td>
                </tr>
                {/* <tr>
                  <td><strong>الإجمالي</strong></td>
                  <td className="text-end">{orderData.total || 0} ريال</td>
                </tr> */}
                <tr>
                  <td><strong>تكلفة الخدمة في المنزل</strong></td>
                  <td className="text-end">{orderData.at_home_service_cost || 0} ريال</td>
                </tr>
              </tbody>
            </Table>
          </CardBody>
        </Card>

   
        {/* Address Section with Map */}
        <Card className="border-0 shadow-sm mb-4">
          <CardBody>
            <h6 className="d-flex align-items-center mb-3">
              <FaMapMarkerAlt className="me-2 text-primary" />
              عنوان العميل
            </h6>
            {provider.location_latitude && provider.location_longitude ? (
              <>
                <div className="mb-3">
                  <AddressMap
                    latitude={provider.location_latitude}
                    longitude={provider.location_longitude}
                  />
                </div>
                <div className="address-details p-3 bg-light rounded mb-3">
                  {formatAddress()}
                </div>
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${provider.location_latitude},${provider.location_longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                >
                  الانتقال إلى الموقع على خرائط Google
                </a>
              </>
            ) : (
              <div className="text-muted">لم يتم تحديد عنوان للطلب</div>
            )}
          </CardBody>
        </Card>

        {/* User and Provider Info */}
        <Row>
          <Col md={6}>
            <Card className="border-0 shadow-sm">
              <CardBody>
                <h6 className="d-flex align-items-center mb-3">
                  <FaUser className="me-2 text-primary" />
                  معلومات العميل
                </h6>
                <p><strong>الاسم:</strong> {user.username || 'غير محدد'}</p>
                <p><strong>الهاتف:</strong> <span dir="ltr">{user.mobile || 'غير محدد'}</span></p>
              </CardBody>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="border-0 shadow-sm">
              <CardBody>
                <h6 className="d-flex align-items-center mb-3">
                  <FaUser className="me-2 text-primary" />
                  معلومات مقدم الخدمة
                </h6>
                <p><strong>الاسم:</strong> {provider.full_name || 'غير محدد'}</p>
                <p><strong>الهاتف:</strong> <span dir="ltr">{provider.mobile || 'غير محدد'}</span></p>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter className="border-top">
        <Button color="secondary" onClick={toggle}>
          إغلاق
        </Button>
      </ModalFooter>
    </Modal>
  );
};

// Helper functions remain the same
const getStatusColor = (status) => {
  const colors = {
    pending: 'warning',
    approved: 'success',
    rejected: 'danger',
    completed: 'primary'
  };
  return colors[status] || 'info';
};

const getPaymentStatusColor = (status) => {
  const colors = {
    paid: 'success',
    failed: 'danger',
    pending: 'warning',
     progress: 'warning'
  };
  return colors[status] || 'info';
};

const translateStatus = (status) => {
  const translations = {
    pending: "قيد الانتظار",
    approved: "تم الموافقة",
    rejected: "ملغى",
    completed: "مكتمل",
     progress: 'قيد التنفيذ'
  };
  return translations[status] || status;
};

const translatePaymentStatus = (status) => {
  const translations = {
    paid: "تم الدفع",
    failed: "فشل الدفع",
    pending: "قيد الانتظار",
     progress: 'قيد التنفيذ'
  };
  return translations[status] || status;
};

OrdersModal.propTypes = {
  toggle: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  orderData: PropTypes.shape({
    id: PropTypes.string,
    booking_status: PropTypes.string,
    paymentStatus: PropTypes.string,
    The_number_of_beneficiaries: PropTypes.number,
    booking_execute_date: PropTypes.object,
    serviceProviderServices: PropTypes.object,
    category_type: PropTypes.string,
    comment: PropTypes.string,
    total: PropTypes.number,
    platform_commission: PropTypes.number,
    total_after_discount: PropTypes.number,
    at_home_service_cost: PropTypes.number,
    paymentMethod: PropTypes.string,
    app_user: PropTypes.object,
    service_providers: PropTypes.object,
    user_address: PropTypes.object,
  }),
};

export default OrdersModal;
